import { useEffect } from 'react';
import { useRouter } from 'next/router';

interface Dialog {
  isShown: boolean;
  show: () => void;
}

/**
 * Custom hook that opens a dialog when a specific query parameter is present in the URL.
 * Automatically removes the query parameter from the URL after opening the dialog.
 *
 * @param paramName - The name of the query parameter to watch (e.g., 'reviews_open')
 * @param dialog - Dialog object with `show()` method and `isShown` boolean
 * @param canOpen - Boolean condition that determines if the dialog can be opened
 */
export const useQueryParamDialog = (paramName: string, dialog: Dialog, canOpen: boolean): void => {
  const router = useRouter();
  const paramValue = router.query[paramName];

  useEffect(() => {
    if (!router.isReady || dialog.isShown || !canOpen) {
      return;
    }

    const shouldOpen = paramValue === 'true';

    if (!shouldOpen) {
      return;
    }

    dialog.show();

    // Remove param from URL
    const { [paramName]: _, ...restQuery } = router.query;

    router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, paramValue, canOpen, dialog.isShown]);
};
